import { FaCheck, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePaymentCompleteMutation } from "../store/api/admin/paymentSlice";
import { useEffect, useState } from "react";
import { updateLogInUser } from "../store/reducer/UserReducer";
import { useDispatch, useSelector } from "react-redux";
import { useRetryPaymentMutation } from "../store/api/school/PaymentHistorySlice";
import { useTranslation } from "react-i18next";

const PaymentComplete = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const resultCode = queryParams.get("resultCode");
  const cardType = queryParams.get("cardType");
  const transactionId = queryParams.get("transactionId");
  const referenceId = queryParams.get("referenceId");
  const amount = queryParams.get("amount");
  const orderNumber = queryParams.get("orderNumber");
  const paymentMethodId = queryParams.get("paymentMethodId");
  const completeToken = queryParams.get("ct");

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;

  const [retryLoading, setRetryLoading] = useState(false);

  // Omni already charged the card by the time this page loads (resultCode
  // comes from Omni's own redirect) — but our backend hasn't necessarily
  // recorded it yet. Don't let the user go to the dashboard (and risk being
  // bounced back to pay again) before our backend has actually confirmed it.
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // subscription
  const [paymentComplete] = usePaymentCompleteMutation();

  const [retryPayment] = useRetryPaymentMutation();

  const handleSubmit = async () => {
    toast.dismiss();
    setConfirmError(null);
    setConfirming(true);

    const payload = {
      referenceId,
      paymentMethodId,
      resultCode: Number(resultCode),
      paymentStatus: "Completed",
      transactionId,
      completeToken,
    };

    try {
      let response = await paymentComplete(payload).unwrap();

      if (response.statusCode === 200) {
        toast.success(response.message);
        dispatch(
          updateLogInUser({
            plan_id: response.data.plan_id,
            is_payment_active: 1,
            payment_status: "Completed",
            is_free_used: 0,
          }),
        );
        setConfirmed(true);
      }
    } catch (err) {
      // Card was already charged by Omni — do NOT send the user back to
      // /pricing-plan here, that would charge them a second time. Surface
      // the failure and let them retry this same confirmation instead.
      const message =
        err?.data?.message || err.message || t("something_went_wrong");
      toast.error(message);
      setConfirmError(message);
    } finally {
      setConfirming(false);
    }
  };

  const handleRetry = async () => {
    toast.dismiss();
    setRetryLoading(true);
    try {
      const response = await retryPayment(orderNumber).unwrap();
      if (response.statusCode === 200 || response.statusCode === 201) {
        window.open(response.data.iframe_url, "_blank");
      }
    } catch (err) {
      toast.error(err?.data?.message || t("retry_failed"));
    } finally {
      setRetryLoading(false);
    }
  };

  useEffect(() => {
    if (resultCode === "0") {
      handleSubmit();
    }
  }, [resultCode]);

  return (
    <div className="payment-complete">
      <div className="p-0">
        {resultCode === "0" ? (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h2>{t("payment_successful_title")}</h2>
              <p>
                {t("paymentSuccessful")}. {t("ConfirmTransaction")}
              </p>

              <div className="order-details">
                <div className="detail-row">
                  <span>{t("orderId")}</span>
                  <span>{orderNumber}</span>
                </div>
                <div className="detail-row">
                  <span>{t("amountPaid")}</span>
                  <span>${amount / 100}</span>
                </div>
                <div className="detail-row">
                  <span>{t("paymentMethod")}</span>
                  <span>{cardType}</span>
                </div>
              </div>

              {confirmError && (
                <p className="text-danger mb-3">
                  {t("verifyingPayment")}
                </p>
              )}

              <div>
                {confirmError ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={confirming}
                    onClick={handleSubmit}
                  >
                    {confirming ? t("retrying") : t("retry")}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={confirming || !confirmed}
                    onClick={() => navigate(`/dashboard`)}
                  >
                    {confirming || !confirmed
                      ? t("confirming_payment")
                      : t("dashboard")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>{t("payment_failed_title")}</h2>
              <p>{t("paymentFailedDesc")}</p>

              <div className="order-details">
                <div className="detail-row">
                  <span>{t("orderId")}</span>
                  <span>{orderNumber}</span>
                </div>
              </div>

              <div>
                {role === "parent" ? (
                  <button
                    type="button"
                    className="btn btn-primary btn-failed"
                    onClick={handleRetry}
                    disabled={retryLoading}
                  >
                    {retryLoading ? (
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="sr-only p-0"></span>
                      </div>
                    ) : (
                      t("tryAgain")
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-failed"
                    onClick={() => navigate(`/pricing-plan`)}
                  >
                    {t("tryAgain")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentComplete;
